<template>
  <BaseModal 
    :show
    size="lg"
    :title="t('app.sysadmin.menu_import.title')"
    @close="emit('close')"
  >
    <div>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Restaurante: <span class="font-medium">{{ restaurant?.name }}</span>
      </p>

      <div class="mt-4 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Archivo JSON
          </label>
          <input
            type="file"
            accept="application/json,.json"
            class="w-full text-sm text-gray-700 dark:text-gray-200 file:mr-4 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-4 file:py-2 file:text-white hover:file:bg-indigo-700"
            @change="handleFileChange"
          >
          <p
            v-if="fileName"
            class="mt-1 text-xs text-gray-500 dark:text-gray-400"
          >
            Seleccionado: {{ fileName }}
          </p>
        </div>

        <div class="space-y-2">
          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              v-model="clearExisting"
              type="checkbox"
              class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            >
            Limpiar menú existente antes de importar
          </label>

          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              v-model="force"
              type="checkbox"
              :disabled="!clearExisting"
              class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
            >
            Forzar borrado aunque existan órdenes activas
          </label>
        </div>

        <div
          v-if="errorMessage"
          class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          {{ errorMessage }}
        </div>
      </div>

      <div class="mt-6 flex justify-end gap-3">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
          :disabled="importing"
          @click="emit('close')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="importing || !parsedMenuData"
          @click="handleImport"
        >
          <span v-if="importing">Importando...</span>
          <span v-else>Importar</span>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseModal from '@/components/common/BaseModal.vue';
import { adminService } from '@/services/adminService';
import { useToast, POSITION } from 'vue-toastification';

const toast = useToast();
const { t } = useI18n();

const props = defineProps<{
  show: boolean;
  restaurant: any | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'imported'): void;
}>();

const importing = ref(false);
const clearExisting = ref(true);
const force = ref(false);
const fileName = ref('');
const parsedMenuData = ref<Record<string, any> | null>(null);
const errorMessage = ref('');

const resetState = () => {
  importing.value = false;
  clearExisting.value = true;
  force.value = false;
  fileName.value = '';
  parsedMenuData.value = null;
  errorMessage.value = '';
};

watch(() => props.show, (isOpen) => {
  if (isOpen) {
    resetState();
  }
});

watch(clearExisting, (enabled) => {
  if (!enabled) {
    force.value = false;
  }
});

const handleFileChange = async (event: Event) => {
  errorMessage.value = '';
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  if (!file) {
    fileName.value = '';
    parsedMenuData.value = null;
    return;
  }

  fileName.value = file.name;

  try {
    const raw = await file.text();
    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.categories)) {
      throw new Error("El JSON debe incluir 'categories' como arreglo");
    }

    parsedMenuData.value = parsed;
  } catch (error: any) {
    parsedMenuData.value = null;
    errorMessage.value = error?.message || 'No se pudo leer el archivo JSON';
  }
};

const handleImport = async () => {
  if (!props.restaurant?.id || !parsedMenuData.value) {
    errorMessage.value = 'Selecciona un archivo JSON válido';
    return;
  }

  importing.value = true;
  errorMessage.value = '';

  try {
    await adminService.importRestaurantMenu({
      restaurant_id: Number(props.restaurant.id),
      clear_existing: clearExisting.value,
      force: force.value,
      menu_data: parsedMenuData.value,
    });

    toast.success('Menú importado correctamente', {
      position: POSITION.TOP_RIGHT,
      timeout: 4000,
    });

    emit('imported');
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'No se pudo importar el menú';
    errorMessage.value = detail;
    toast.error(detail, {
      position: POSITION.TOP_RIGHT,
      timeout: 5000,
    });
  } finally {
    importing.value = false;
  }
};
</script>
